export type Story = {
  id: string
  title: string
  subtitle: string
  description: string
  age?: string
  ageMin?: number | null
  ageMax?: number | null
  minutes: number
  mood: '잠자리' | '다정한 마음' | '용기' | '상상 여행'
  cover: 'moon' | 'flower' | 'dream' | 'ocean' | 'forest' | 'star'
  audio?: string
  transcript: string[]
  coverUrl?: string
  versionId?: string
  durationSeconds?: number
  generationUnits?: number
  accessSummary?: string | Record<string, unknown>
  sampleUrl?: string
  sourceKind?: 'text' | 'audio'
  /** Selected by the published story version. Older catalogs infer this from sourceKind. */
  renderMode?: 'tts' | 'voice_conversion' | 'renarration'
  sourceAudio?: { url?: string; sourcePage?: string; label?: string }
  conversionStatus?: 'ready' | 'rights_unconfirmed' | 'engine_unavailable' | 'source_unavailable' | 'quality_unverified' | 'quality_failed' | 'experimental_unverified'
  usageGuidance?: string | null
  sourcePage?: string
  sourceLabel?: string
  conversionAvailable?: boolean
  experimentalPreviewAvailable?: boolean
  conversionUnavailableReason?: string | null
  developmentOnly?: boolean
}

export const hasStoryAge = (story: Story) => Number.isInteger(story.ageMin) && Number.isInteger(story.ageMax) && Number(story.ageMin) >= 0 && Number(story.ageMin) <= Number(story.ageMax) && Number(story.ageMax) <= 18
export const storyAgeLabel = (story: Story) => hasStoryAge(story) ? story.ageMin === story.ageMax ? `${story.ageMin}세` : `${story.ageMin}–${story.ageMax}세` : '연령 안내 준비 중'
export const storyIncludesAge = (story: Story, age: number) => hasStoryAge(story) && Number(story.ageMin) <= age && Number(story.ageMax) >= age

export const stories: Story[] = [
  {
    id: 'moon-rabbit',
    title: '달빛을 모으는 토끼',
    subtitle: '구름 위에서 만나는 토끼와 작은 별',
    description: '달빛을 친구에게 선물하고 싶은 작은 토끼가 구름 너머로 떠나는 포근한 잠자리 이야기예요.',
    age: '4–7세', ageMin: 4, ageMax: 7, minutes: 5, mood: '잠자리', cover: 'moon',
    transcript: [
      '숲 가장 높은 언덕에는 달빛을 모으는 작은 토끼, 루루가 살았어요.',
      '루루는 밤마다 유리병을 들고 언덕에 올라가 반짝이는 달빛을 한 줌씩 담았답니다.',
      '어느 날, 늘 씩씩하던 곰 친구 모모가 어두운 밤이 무섭다고 말했어요.',
      '루루는 가장 따뜻한 달빛을 골라 작은 주머니에 담고 모모의 집으로 향했지요.',
      '둘은 창가에 달빛 주머니를 걸고, 구름이 천천히 흘러가는 모습을 함께 바라보았어요.',
      '혼자일 때 커 보이던 그림자도 둘이 바라보니 춤추는 나뭇가지처럼 보였답니다.',
      '모모는 이제 괜찮다며 루루의 손을 꼭 잡았어요.',
      '그날부터 루루는 알게 되었어요. 달빛보다 더 환한 것은 곁을 지켜 주는 마음이라는 걸요.',
      '창밖의 달도 고개를 끄덕이며 두 친구에게 잘 자라고 인사했답니다.'
    ]
  },
  {
    id: 'little-flower', title: '작은 꽃의 큰 인사', subtitle: '용기를 내어 건넨 다정한 한마디',
    description: '말을 건네기 어려웠던 작은 곰이 꽃 한 송이와 함께 처음 인사하는 이야기예요.',
    age: '3–5세', ageMin: 3, ageMax: 5, minutes: 4, mood: '다정한 마음', cover: 'flower',
    transcript: ['작은 곰 보보는 매일 토끼가 노는 꽃밭을 바라보았어요.', '오늘은 꼭 인사하자고 마음먹었지만 목소리가 나오지 않았지요.', '보보는 가장 예쁜 데이지 한 송이를 골라 조심스레 내밀었어요.', '토끼는 활짝 웃으며 말했어요. 같이 꽃밭을 걸을래?', '작은 인사 하나가 두 친구의 긴 이야기를 열어 주었답니다.']
  },
  {
    id: 'blanket-cloud', title: '이불 구름 기차', subtitle: '폭신한 구름을 타고 떠나는 여행',
    description: '잠이 오지 않는 아이가 이불 구름 기차를 타고 별 마을을 여행해요.',
    age: '4–7세', ageMin: 4, ageMax: 7, minutes: 7, mood: '상상 여행', cover: 'dream',
    transcript: ['창문 틈으로 폭신한 이불 구름 기차가 도착했어요.', '기차는 하품 터널을 지나 별 마을로 천천히 달렸답니다.']
  },
  {
    id: 'whale-song', title: '고래가 부른 자장가', subtitle: '푸른 바다 아래 잔잔한 노래',
    description: '길을 잃은 작은 물고기가 고래의 노래를 따라 집으로 돌아가는 이야기예요.',
    age: '3–5세', ageMin: 3, ageMax: 5, minutes: 6, mood: '잠자리', cover: 'ocean',
    transcript: ['바다가 조용해지자 커다란 고래가 낮고 포근한 노래를 불렀어요.']
  },
  {
    id: 'acorn-mail', title: '도토리 우체국', subtitle: '마음을 전하는 숲속 편지',
    description: '다람쥐 우체부가 숲 친구들의 마음을 담은 편지를 배달해요.',
    age: '6–9세', ageMin: 6, ageMax: 9, minutes: 9, mood: '다정한 마음', cover: 'forest',
    transcript: ['숲이 깨어나기 전, 도토리 우체국의 작은 종이 울렸어요.']
  },
  {
    id: 'pocket-star', title: '주머니 속 작은 별', subtitle: '작아도 반짝일 수 있어',
    description: '빛을 잃었다고 생각한 별이 아이의 주머니에서 다시 용기를 찾는 이야기예요.',
    age: '6–9세', ageMin: 6, ageMax: 9, minutes: 8, mood: '용기', cover: 'star',
    transcript: ['아이는 길가에서 아주 작게 깜빡이는 별 하나를 발견했어요.']
  }
]

export const recordingSentences = [
  '달빛이 창문으로 들어왔어요.',
  '작은 토끼가 친구에게 인사했어요.',
  '잘 자, 내일도 함께 놀자.',
  '바람이 나뭇잎을 흔들었어요.',
  '파란 새가 숲길을 날아갔어요.',
  '따뜻한 햇살이 비쳤어요.',
  '아이는 반짝이는 돌을 주웠어요.',
  '멀리서 종소리가 들려왔어요.',
  '할머니가 웃으며 손을 흔들었어요.',
  '오늘은 어떤 꿈을 꿀까요?'
]
